import React from 'react';
import type { VideoDuration } from '../types/api';

interface DurationToggleProps {
  value: VideoDuration;
  onChange: (next: VideoDuration) => void;
  disabled?: boolean;
}

/**
 * Segmented 5s / 10s duration picker.
 *
 * Visibility is gated upstream: this component should only be rendered
 * when `userData.can_select_duration === true` (server-computed).
 * See docs/duration-toggle-plan.md.
 */
export function DurationToggle({ value, onChange, disabled = false }: DurationToggleProps) {
  const options: VideoDuration[] = [5, 10];

  return (
    <div className="flex flex-col items-start gap-2">
      <label className="text-text-muted text-body-sm">Duration</label>
      <div
        role="radiogroup"
        aria-label="Video duration"
        className="inline-flex bg-bg-surface rounded-button p-1 border border-white/10"
      >
        {options.map((opt) => {
          const selected = value === opt;
          return (
            <button
              key={opt}
              type="button"
              role="radio"
              aria-checked={selected}
              disabled={disabled}
              onClick={() => {
                if (!selected && !disabled) onChange(opt);
              }}
              className={[
                'min-w-[88px] min-h-[44px] px-6 py-2 rounded-button',
                'text-body font-medium transition-colors',
                'focus:outline-none focus-visible:ring-2 focus-visible:ring-accent',
                selected
                  ? 'bg-white text-black'
                  : 'text-text-muted hover:text-white',
                disabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer',
              ].join(' ')}
            >
              {opt}s
            </button>
          );
        })}
      </div>
    </div>
  );
}

export default DurationToggle;
